import { matchPath } from "react-router"

import { getRoutes } from "../routes"

// Readable labels for the Mixpanel report, keyed by route pattern rather than by
// a literal URL so renaming a page is a one-line change here. Patterns missing
// from this map fall back to a name derived from the pattern itself, so a newly
// added route is still reported instead of silently becoming "Unknown".
const PAGE_LABELS = {
  "/": "Home",
  "/map": "Map",
  "/dashboard": "Dashboard",
  "/insights": "Insights",
  "/insights/data-validation": "Insights - Data validation",
  "/insights/single": "Insights - Single",
  "/report": "Reports",
  "/reports": "Reports list",
  "/reports/:action": "Reports list - Action",
  "/report-details/:id": "Report details",
  "/report-details/:id/:action": "Report details - Action",
  "/devices": "Devices",
  "/nrw": "Non-revenue water",
  "/pressure": "Pressure",
  "/plan": "Plans",
  "/customers": "Customer list",
  "/customer/:id": "Customer detail",
  "/customer/:id/overview": "Customer overview",
  "/customer/:id/usage": "Customer usage",
  "/customer/:id/pressure-usage": "Customer pressure usage",
  "/customer/:id/actions": "Customer actions",
  "/customer/:id/timeline": "Customer timeline",
  "/customer/:id/alerts": "Customer alerts",
  "/industry": "Industry list",
  "/industry/:id": "Industry detail",
  "/alerts": "Alerts",
  "/notes": "Notes",
  "/users": "User list",
  "/user/add": "Add user",
  "/user/:id/edit": "Edit user",
  "/import-dashboard": "Import dashboard",
  "/alert-import/:id": "Import detail",
  "/settings": "Settings",
  "/settings/notifications": "Settings - Notifications",
  "/settings/provide-feedback": "Settings - Provide feedback",
  "/settings/contact-address": "Settings - Contact address",
  "/settings/roles": "Settings - Roles",
  "/management/tenants": "Tenants management",
  "/sign-in": "Sign in",
  "/sign-out": "Sign out",
  "/verification": "Verification",
  "/tv-app": "TV app",
  "/tv-app-test": "TV app test",
  "/shared-customer/:customerId": "Shared customer",
  "/screenshot-alert-hw": "Screenshot alert - HW",
  "/screenshot-alert-aveo": "Screenshot alert - Aveo",
  "/screenshot-alert-residential": "Screenshot alert - Residential",
  "/test": "Test",
}

const derivePageName = (pattern) => {
  const segments = pattern
    .split("/")
    .filter((segment) => segment && !segment.startsWith(":"))

  if (segments.length === 0) return "Home"

  const label = segments.join(" ").replace(/[-_]+/g, " ")
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// Parent routes render their children at `${parent.path}${child.path}` (see
// insights-page.js and setting-receive-mail-page.jsx), so patterns are built by
// concatenating the same way.
const flattenRoutes = (routes, parentPath = "") =>
  routes.reduce((acc, route) => {
    if (!route || !route.path) return acc

    const children = route.routes || []
    const pattern = `${parentPath}${route.path}`

    // A parent that renders children can never be an exact match on its own.
    acc.push({ pattern, exact: Boolean(route.exact) && children.length === 0 })

    return children.length > 0
      ? acc.concat(flattenRoutes(children, pattern))
      : acc
  }, [])

let cachedPatterns = null

const getPatterns = () => {
  if (cachedPatterns === null) {
    // Longest pattern first, so `/customer/:id/usage` is preferred over the
    // non-exact parent `/customer/:id`.
    cachedPatterns = flattenRoutes(getRoutes()).sort(
      (a, b) => b.pattern.length - a.pattern.length
    )
  }
  return cachedPatterns
}

// Resolves a pathname to { page, route }. `route` is the pattern, which stays
// stable for pages carrying an id and is what reports should group by.
export const getPageInfo = (pathname) => {
  try {
    const matched = getPatterns().find(({ pattern, exact }) =>
      matchPath(pathname, { path: pattern, exact })
    )

    return matched
      ? {
          page: PAGE_LABELS[matched.pattern] || derivePageName(matched.pattern),
          route: matched.pattern,
        }
      : { page: "Unknown", route: pathname }
  } catch (error) {
    console.error("getPageInfo", error)
    return { page: "Unknown", route: pathname }
  }
}
